import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { obtenerMedidasPaginaContrato } from '../utils/contratoPaginaA4';
import { calcularSaltosPaginaContrato, alturaContenidoPagina } from '../utils/contratoPaginacionPreview';

/** Marca de agua centrada en una hoja A4 (una instancia por página). */
function MarcaDeAguaPagina({ logoSrc, opacidad = 0.1, anchoPorcentaje = 58 }) {
  if (!logoSrc) return null;

  return (
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 2,
        pointerEvents: 'none',
        overflow: 'hidden',
      }}
    >
      <img
        src={logoSrc}
        alt=""
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: `${anchoPorcentaje}%`,
          maxHeight: '72%',
          objectFit: 'contain',
          opacity: opacidad,
        }}
      />
    </div>
  );
}

/**
 * Vista previa del contrato en hojas A4 apiladas (recorte por ventana desplazada).
 */
const ContratoVistaPreviaPaginada = ({ htmlContenido, logoSrc, fontFamily }) => {
  const medidasBase = useMemo(() => obtenerMedidasPaginaContrato(), []);
  const containerRef = useRef(null);
  const measureRef = useRef(null);
  const [saltosPagina, setSaltosPagina] = useState([0]);
  const [alturaTotal, setAlturaTotal] = useState(0);
  const [anchoVista, setAnchoVista] = useState(null);

  const anchoEfectivo = anchoVista ?? medidasBase.anchoPx;
  const medidas = useMemo(() => {
    const escala = anchoEfectivo / medidasBase.anchoPx;
    return {
      anchoPx: anchoEfectivo,
      altoPx: medidasBase.altoPx * escala,
      paddingTopPx: medidasBase.paddingTopPx * escala,
      paddingBottomPx: medidasBase.paddingBottomPx * escala,
      paddingLeftPx: medidasBase.paddingLeftPx * escala,
      paddingRightPx: medidasBase.paddingRightPx * escala,
      anchoContenidoPx: medidasBase.anchoContenidoPx * escala,
      altoContenidoPx: medidasBase.altoContenidoPx * escala,
    };
  }, [anchoEfectivo, medidasBase]);

  const numPaginas = saltosPagina.length;

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const actualizarAncho = () => {
      const disponible = container.clientWidth || medidasBase.anchoPx;
      setAnchoVista(Math.min(medidasBase.anchoPx, Math.max(disponible, 0)));
    };

    actualizarAncho();

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(actualizarAncho);
      observer.observe(container);
      return () => observer.disconnect();
    }

    return undefined;
  }, [medidasBase.anchoPx]);

  useLayoutEffect(() => {
    const el = measureRef.current;
    if (!el) return;

    const actualizar = () => {
      const total = el.scrollHeight || el.offsetHeight || 0;
      const saltos = calcularSaltosPaginaContrato(el, medidas.altoContenidoPx);
      setAlturaTotal(total);
      setSaltosPagina(saltos.length > 0 ? saltos : [0]);
    };

    actualizar();

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(actualizar);
      observer.observe(el);
      return () => observer.disconnect();
    }

    return undefined;
  }, [htmlContenido, medidas.altoContenidoPx, medidas.anchoContenidoPx]);

  const estiloPagina = {
    width: `${medidas.anchoPx}px`,
    maxWidth: '100%',
    height: `${medidas.altoPx}px`,
    padding: `${medidas.paddingTopPx}px ${medidas.paddingRightPx}px ${medidas.paddingBottomPx}px ${medidas.paddingLeftPx}px`,
    boxSizing: 'border-box',
    position: 'relative',
    overflow: 'hidden',
    background: '#fff',
    boxShadow: '0 4px 14px rgba(15, 23, 42, 0.12)',
    border: '1px solid #e5e7eb',
    fontFamily,
  };

  return (
    <div ref={containerRef} className="flex flex-col items-center gap-2 w-full min-w-0 max-w-full">
      {/* Medición invisible del contenido */}
      <div
        aria-hidden="true"
        style={{
          position: 'fixed',
          left: '-12000px',
          top: 0,
          width: `${medidas.anchoContenidoPx}px`,
          opacity: 0,
          pointerEvents: 'none',
          zIndex: -1,
          fontFamily,
        }}
      >
        <div ref={measureRef} dangerouslySetInnerHTML={{ __html: htmlContenido }} />
      </div>

      {saltosPagina.map((offsetY, indice) => {
        const altoVisible = alturaContenidoPagina(
          saltosPagina,
          indice,
          medidas.altoContenidoPx,
          alturaTotal
        );

        return (
        <div key={`pagina-${indice}-${offsetY}`} className="flex flex-col items-center gap-1 w-full min-w-0 max-w-full">
          <div className="contrato-pagina-a4-preview" style={estiloPagina}>
            <div
              style={{
                position: 'relative',
                zIndex: 1,
                width: '100%',
                maxWidth: '100%',
                height: `${altoVisible}px`,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  marginTop: `${-offsetY}px`,
                  position: 'relative',
                  width: '100%',
                  maxWidth: '100%',
                }}
                dangerouslySetInnerHTML={{ __html: htmlContenido }}
              />
            </div>
            <MarcaDeAguaPagina logoSrc={logoSrc} />
          </div>
          <span
            className="text-[9px] text-gray-400"
            style={{ fontFamily }}
          >
            Pág. {indice + 1} / {numPaginas}
          </span>
        </div>
        );
      })}

      <p className="text-[10px] text-gray-400 text-center px-2 mt-2">
        Vista previa aproximada en hojas A4. Al exportar el PDF pueden variar ligeramente los cortes.
      </p>
    </div>
  );
};

export default ContratoVistaPreviaPaginada;
